import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  socialLogin: (data) => api.post('/auth/social-login', data),
  getMe: () => api.get('/auth/me'),
  generateQRToken: () => api.post('/auth/qr-token'),
};

// Stores API
export const storesAPI = {
  list: (skip = 0, limit = 50) => api.get(`/stores?skip=${skip}&limit=${limit}`),
  get: (storeId) => api.get(`/stores/${storeId}`),
  create: (data) => api.post('/stores', data),
  update: (storeId, data) => api.patch(`/stores/${storeId}`, data),
};

// Branding API
export const brandingAPI = {
  get: (storeId) => api.get(`/branding/${storeId}`),
  update: (storeId, data) => api.patch(`/branding/${storeId}`, data),
};

// Levels API
export const levelsAPI = {
  list: (storeId) => api.get(`/levels/${storeId}`),
  create: (storeId, data) => api.post(`/levels/${storeId}`, data),
  update: (storeId, levelId, data) => api.patch(`/levels/${storeId}/${levelId}`, data),
  delete: (storeId, levelId) => api.delete(`/levels/${storeId}/${levelId}`),
};

// Rewards API
export const rewardsAPI = {
  list: (storeId, levelId = null) => {
    const url = levelId ? `/rewards/${storeId}?level_id=${levelId}` : `/rewards/${storeId}`;
    return api.get(url);
  },
  create: (storeId, data) => api.post(`/rewards/${storeId}`, data),
  update: (storeId, rewardId, data) => api.patch(`/rewards/${storeId}/${rewardId}`, data),
  delete: (storeId, rewardId) => api.delete(`/rewards/${storeId}/${rewardId}`),
};

// Promotions API
export const promotionsAPI = {
  list: (storeId, activeOnly = true) => api.get(`/promotions/${storeId}?active_only=${activeOnly}`),
  listAll: (storeId) => api.get(`/promotions/${storeId}/all`),
  create: (storeId, data) => api.post(`/promotions/${storeId}`, data),
  update: (storeId, promoId, data) => api.patch(`/promotions/${storeId}/${promoId}`, data),
  delete: (storeId, promoId) => api.delete(`/promotions/${storeId}/${promoId}`),
};

// Visits API
export const visitsAPI = {
  register: (storeId, qrToken) => api.post(`/visits/${storeId}`, { customer_qr_token: qrToken }),
  list: (storeId, customerId = null, limit = 50) => {
    let url = `/visits/${storeId}?limit=${limit}`;
    if (customerId) url += `&customer_id=${customerId}`;
    return api.get(url);
  },
  listToday: (storeId) => api.get(`/visits/${storeId}/today`),
};

// Redemptions API
export const redemptionsAPI = {
  create: (storeId, customerId, rewardId) => 
    api.post(`/redemptions/${storeId}`, { customer_id: customerId, reward_id: rewardId }),
  list: (storeId, customerId = null, status = null, limit = 50) => {
    let url = `/redemptions/${storeId}?limit=${limit}`;
    if (customerId) url += `&customer_id=${customerId}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
};

// Wallet API
export const walletAPI = {
  get: (storeId) => api.get(`/wallet/${storeId}`),
};

// Users API
export const usersAPI = {
  listStaff: (storeId) => api.get(`/users/${storeId}/staff`),
  addStaff: (storeId, email, role = 'store_staff') => 
    api.post(`/users/${storeId}/staff?email=${encodeURIComponent(email)}&role=${role}`),
  removeStaff: (storeId, userId) => api.delete(`/users/${storeId}/staff/${userId}`),
};

// Program Rules API
export const rulesAPI = {
  get: (storeId) => api.get(`/rules/${storeId}`),
  update: (storeId, data) => api.patch(`/rules/${storeId}`, data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (storeId) => api.get(`/dashboard/${storeId}/stats`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  listStores: () => api.get('/admin/stores'),
  toggleStoreStatus: (storeId, isActive) => 
    api.patch(`/admin/stores/${storeId}/status?is_active=${isActive}`),
};

// Customer Lookup API (for Store App)
export const lookupAPI = {
  customer: (storeId, qrToken) => 
    api.get(`/customers/lookup?store_id=${storeId}&qr_token=${encodeURIComponent(qrToken)}`),
};

// Health Check
export const healthAPI = {
  check: () => api.get('/health'),
};

export default api;
